import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import LiveCard from "@/components/base/LiveCard";
import { liveItems } from "@/mocks/lives";
import { searchLives, sortLives } from "@/utils/live";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [activeType, setActiveType] = useState<"live">("live");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentTitle(
    `搜索结果 - “${query || "全部"}” - 360直播`,
    `360直播搜索结果：与“${query || "全部"}”相关的直播内容，涵盖游戏、娱乐、体育等多频道高清直播。`
  );

  const results = useMemo(() => searchLives(liveItems, query), [query]);
  const hotRecommend = useMemo(() => sortLives(liveItems, "hot").slice(0, 4), []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

      <div className="mt-5 mb-7">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          搜索结果
        </h1>
        <p className="mt-3 text-sm text-foreground-600">
          {query ? (
            <>
              关键词 <span className="font-semibold text-primary-300">“{query}”</span> 的搜索结果
            </>
          ) : (
            "输入关键词，搜索你感兴趣的直播或主播"
          )}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4">
            <i className="ri-search-line text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索直播标题或主播名，如：解说、钢琴、夜市"
              className="h-11 w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line" />
            重新搜索
          </button>
        </div>
      </form>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-foreground-600">
          {query ? (
            <>
              共找到 <span className="font-semibold text-primary-300">{results.length}</span> 个结果
            </>
          ) : (
            "等待输入关键词"
          )}
        </p>
        <div className="flex items-center gap-1 rounded-full bg-background-200/70 px-1 py-1">
          <button
            type="button"
            onClick={() => setActiveType("live")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              activeType === "live"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:text-foreground-950"
            }`}
          >
            仅直播
          </button>
        </div>
      </div>

      {results.length > 0 ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {results.map((item) => (
            <LiveCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-background-200/60 bg-background-100 p-8 md:p-12">
          <div className="flex flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary-100 text-foreground-500">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              {query ? "未找到相关直播" : "开始搜索精彩直播"}
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              {query
                ? "换个关键词试试，或者看看下面的热门直播推荐。"
                : "在搜索框中输入直播标题或主播名，即可快速定位直播内容。"}
            </p>
          </div>

          <div className="mt-8">
            <h3 className="mb-4 font-heading text-base font-bold text-foreground-950">热门直播推荐</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {hotRecommend.map((item) => (
                <LiveCard key={item.id} item={item} />
              ))}
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <Link
              to="/channels"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-live-line" />
              浏览全部频道
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}